// פעולות עריכה מותרות. המודל מחזיר רק פעולות מהרשימה הזו, על מזהי שכבות קיימים.
// אותן פעולות משמשות גם לעריכה ידנית (גרירה, מהירות) — בלי AI.
import { z } from 'zod';
import { AnimProps, BannerDoc, Easing, Keyframe, Transition, type Layer } from './banner';

const layerId = z.string().min(1).max(64);

export const EditOp = z.discriminatedUnion('op', [
  z.object({ op: z.literal('move'), layerId, x: z.number().optional(), y: z.number().optional() }),
  z.object({ op: z.literal('resize'), layerId, width: z.number().min(1).optional(), height: z.number().min(1).optional() }),
  // הגדלה יחסית: משנה מידות ופונט יחד, סביב המרכז
  z.object({ op: z.literal('scale'), layerId, factor: z.number().min(0.1).max(10) }),
  z.object({ op: z.literal('rotate'), layerId, rotation: z.number().min(-360).max(360) }),
  z.object({ op: z.literal('setOpacity'), layerId, opacity: z.number().min(0).max(1) }),
  z.object({ op: z.literal('setVisible'), layerId, visible: z.boolean() }),
  z.object({ op: z.literal('setZIndex'), layerId, zIndex: z.number().int().min(0).max(999) }),
  z.object({ op: z.literal('setText'), layerId, text: z.string().max(500) }),
  z.object({
    op: z.literal('setTextStyle'), layerId,
    style: z.object({
      fontSize: z.number().min(4).max(400).optional(),
      fontWeight: z.number().int().min(100).max(900).optional(),
      lineHeight: z.number().min(0.5).max(4).optional(),
      align: z.enum(['start', 'center', 'end']).optional(),
      verticalAlign: z.enum(['top', 'middle', 'bottom']).optional(),
      color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
      direction: z.enum(['rtl', 'ltr', 'auto']).optional(),
      letterSpacing: z.number().min(-10).max(50).optional(),
    }),
  }),
  z.object({ op: z.literal('setFill'), layerId, fill: z.string().regex(/^#[0-9a-fA-F]{6}$/) }),
  z.object({ op: z.literal('setImageFit'), layerId, fit: z.enum(['contain', 'cover']).optional(), focalX: z.number().min(0).max(1).optional(), focalY: z.number().min(0).max(1).optional() }),
  z.object({ op: z.literal('setTiming'), layerId, startMs: z.number().int().min(0).optional(), endMs: z.number().int().min(0).nullable().optional() }),
  z.object({ op: z.literal('setEnter'), layerId, enter: Transition.nullable() }),
  z.object({ op: z.literal('setExit'), layerId, exit: Transition.nullable() }),
  z.object({ op: z.literal('setKeyframes'), layerId, keyframes: z.array(Keyframe).max(50) }),
  z.object({ op: z.literal('setBannerTiming'), durationMs: z.number().int().min(100).max(120000).optional(), loops: z.number().int().min(0).max(100).optional(), endFrameMs: z.number().int().min(0).nullable().optional() }),
  // שינוי מהירות כולל: מכפיל את כל הזמנים
  z.object({ op: z.literal('setSpeed'), factor: z.number().min(0.1).max(10) }),
  z.object({ op: z.literal('setBackgroundColor'), color: z.string().regex(/^#[0-9a-fA-F]{6}$/) }),
  // פונט: setFontKey מבקש פונט מהמאגר (השרת מתרגם אותו ל-setFont עם נכסים אמיתיים לפני ההחלה)
  z.object({ op: z.literal('setFontKey'), layerId, fontKey: z.string().min(1).max(60) }),
  z.object({ op: z.literal('setFont'), layerId, family: z.string().min(1).max(100), fontAssetId: z.string().nullable(), assetIds: z.array(z.string()).max(20) }),
  // הוספה ומחיקה של אלמנטים (עריכה חופשית בפרומט)
  z.object({ op: z.literal('removeLayer'), layerId }),
  z.object({ op: z.literal('addShape'), id: z.string().max(64).optional(), name: z.string().max(80).optional(), x: z.number(), y: z.number(), width: z.number().min(1), height: z.number().min(1), fill: z.string().regex(/^#[0-9a-fA-F]{6}$/), radius: z.number().min(0).max(1000).optional(), zIndex: z.number().int().min(0).max(999).optional(), opacity: z.number().min(0).max(1).optional() }),
  z.object({ op: z.literal('addText'), id: z.string().max(64).optional(), name: z.string().max(80).optional(), text: z.string().min(1).max(500), x: z.number(), y: z.number(), width: z.number().min(1), height: z.number().min(1), fontSize: z.number().min(4).max(400), fontWeight: z.number().int().min(100).max(900).optional(), color: z.string().regex(/^#[0-9a-fA-F]{6}$/), align: z.enum(['start', 'center', 'end']).optional(), zIndex: z.number().int().min(0).max(999).optional(), fontKey: z.string().max(60).optional(), family: z.string().max(100).optional(), fontAssetId: z.string().nullable().optional(), assetIds: z.array(z.string()).max(20).optional() }),
]);
export type EditOp = z.infer<typeof EditOp>;

export const EditProposal = z.object({
  baseRevision: z.number().int().min(0),
  summary: z.string().max(1000),
  ops: z.array(EditOp).min(1).max(100),
});
export type EditProposal = z.infer<typeof EditProposal>;

export class OpError extends Error {}

export interface ApplyOptions {
  /** האם המבצע הוא AI — אז טקסט נעול חסום */
  actor: 'user' | 'ai';
}

function uniqueId(doc: BannerDoc, base: string): string {
  const clean = base.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 50) || 'layer';
  let id = clean;
  for (let i = 2; doc.layers.some((x) => x.id === id); i++) id = `${clean}_${i}`;
  return id;
}
const topZ = (doc: BannerDoc) => Math.min(999, Math.max(0, ...doc.layers.map((x) => x.zIndex)) + 1);

function findLayer(doc: BannerDoc, id: string): Layer {
  const l = doc.layers.find((x) => x.id === id);
  if (!l) throw new OpError(`שכבה לא קיימת: ${id}`);
  return l;
}

function track(doc: BannerDoc, layerId: string) {
  let t = doc.animationTracks.find((x) => x.layerId === layerId);
  if (!t) {
    t = { layerId, enter: null, exit: null, keyframes: [] };
    doc.animationTracks.push(t);
  }
  return t;
}

const r = (n: number) => Math.round(n * 100) / 100;

/** מחיל פעולות על עותק של המסמך ומחזיר מסמך חדש מאומת. זורק OpError על פעולה אסורה. */
export function applyOps(input: BannerDoc, ops: EditOp[], opts: ApplyOptions): BannerDoc {
  const doc: BannerDoc = structuredClone(input);
  for (const op of ops) {
    switch (op.op) {
      case 'setSpeed': {
        const f = 1 / op.factor; // מהירות x2 = זמנים חצי
        const t = (v: number) => Math.max(0, Math.round(v * f));
        doc.durationMs = Math.max(100, t(doc.durationMs));
        if (doc.endFrameMs != null) doc.endFrameMs = t(doc.endFrameMs);
        for (const l of doc.layers) { l.startMs = t(l.startMs); if (l.endMs != null) l.endMs = Math.max(l.startMs + 1, t(l.endMs)); }
        for (const tr of doc.animationTracks) {
          if (tr.enter) tr.enter.durationMs = t(tr.enter.durationMs);
          if (tr.exit) tr.exit.durationMs = t(tr.exit.durationMs);
          for (const k of tr.keyframes) k.atMs = t(k.atMs);
        }
        break;
      }
      case 'setBannerTiming':
        if (op.durationMs !== undefined) doc.durationMs = op.durationMs;
        if (op.loops !== undefined) doc.loops = op.loops;
        if (op.endFrameMs !== undefined) doc.endFrameMs = op.endFrameMs;
        break;
      case 'setBackgroundColor':
        doc.background.color = op.color;
        break;
      case 'addShape': {
        if (doc.layers.length >= 60) throw new OpError('יותר מדי שכבות במסמך');
        const id = uniqueId(doc, op.id ?? 'shape');
        doc.layers.push({ id, name: op.name ?? 'צורה', type: 'shape', role: 'decor', x: r(op.x), y: r(op.y), width: r(op.width), height: r(op.height), zIndex: op.zIndex ?? topZ(doc), rotation: 0, opacity: op.opacity ?? 1, visible: true, locked: false, startMs: 0, endMs: null, content: { fill: op.fill, radius: op.radius ?? 0, borderColor: null, borderWidth: 0 } } as any);
        break;
      }
      case 'addText': {
        if (doc.layers.length >= 60) throw new OpError('יותר מדי שכבות במסמך');
        if (op.fontKey && !op.family) throw new OpError(`הפונט ${op.fontKey} לא נפתר`);
        const base = doc.layers.find((x) => x.type === 'text') as any;
        const id = uniqueId(doc, op.id ?? 'text');
        doc.layers.push({ id, name: op.name ?? 'טקסט', type: 'text', role: 'decor', x: r(op.x), y: r(op.y), width: r(op.width), height: r(op.height), zIndex: op.zIndex ?? topZ(doc), rotation: 0, opacity: 1, visible: true, locked: false, startMs: 0, endMs: null,
          content: { text: op.text, direction: base?.content.direction ?? 'rtl', fontAssetId: op.family ? (op.fontAssetId ?? null) : (base?.content.fontAssetId ?? null), fontFamily: op.family ?? base?.content.fontFamily ?? 'Heebo', fontWeight: op.fontWeight ?? 700, fontSize: op.fontSize, lineHeight: 1.15, align: op.align ?? 'center', verticalAlign: 'middle', color: op.color, letterSpacing: 0, copyKey: null, locked: false } } as any);
        for (const a of op.assetIds ?? []) if (!doc.assetIds.includes(a)) doc.assetIds.push(a);
        break;
      }
      default: {
        const l = findLayer(doc, op.layerId);
        if (l.locked && opts.actor === 'ai' && op.op !== 'setVisible') {
          throw new OpError(`השכבה ${l.id} נעולה`);
        }
        switch (op.op) {
          case 'move': if (op.x !== undefined) l.x = r(op.x); if (op.y !== undefined) l.y = r(op.y); break;
          case 'resize': if (op.width !== undefined) l.width = r(op.width); if (op.height !== undefined) l.height = r(op.height); break;
          case 'scale': {
            const cx = l.x + l.width / 2, cy = l.y + l.height / 2;
            l.width = r(l.width * op.factor); l.height = r(l.height * op.factor);
            l.x = r(cx - l.width / 2); l.y = r(cy - l.height / 2);
            if (l.type === 'text' || l.type === 'button') l.content.fontSize = r(l.content.fontSize * op.factor);
            break;
          }
          case 'rotate': l.rotation = op.rotation; break;
          case 'setOpacity': l.opacity = op.opacity; break;
          case 'setVisible': l.visible = op.visible; break;
          case 'setZIndex': l.zIndex = op.zIndex; break;
          case 'setText':
            if (l.type !== 'text' && l.type !== 'button') throw new OpError(`setText על שכבה שאינה טקסט: ${l.id}`);
            if (l.content.locked && opts.actor === 'ai') throw new OpError(`הטקסט בשכבה ${l.id} קובע בקמפיין ואסור לעריכת AI`);
            l.content.text = op.text;
            break;
          case 'setTextStyle':
            if (l.type !== 'text' && l.type !== 'button') throw new OpError(`setTextStyle על שכבה שאינה טקסט: ${l.id}`);
            Object.assign(l.content, Object.fromEntries(Object.entries(op.style).filter(([, v]) => v !== undefined)));
            break;
          case 'setFill':
            if (l.type === 'shape' || l.type === 'button') l.content.fill = op.fill;
            else throw new OpError(`setFill על שכבה ללא מילוי: ${l.id}`);
            break;
          case 'setImageFit':
            if (l.type !== 'image') throw new OpError(`setImageFit על שכבה שאינה תמונה: ${l.id}`);
            if (op.fit) l.content.fit = op.fit;
            if (op.focalX !== undefined) l.content.focalX = op.focalX;
            if (op.focalY !== undefined) l.content.focalY = op.focalY;
            break;
          case 'setTiming':
            if (op.startMs !== undefined) l.startMs = op.startMs;
            if (op.endMs !== undefined) l.endMs = op.endMs;
            break;
          case 'setFontKey': throw new OpError(`הפונט ${op.fontKey} לא נפתר`);
          case 'setFont':
            if (l.type !== 'text' && l.type !== 'button') throw new OpError(`setFont על שכבה שאינה טקסט: ${l.id}`);
            l.content.fontFamily = op.family; l.content.fontAssetId = op.fontAssetId;
            for (const a of op.assetIds) if (!doc.assetIds.includes(a)) doc.assetIds.push(a);
            break;
          case 'removeLayer':
            doc.layers = doc.layers.filter((x) => x.id !== l.id);
            doc.animationTracks = doc.animationTracks.filter((t) => t.layerId !== l.id);
            break;
          case 'setEnter': track(doc, l.id).enter = op.enter; break;
          case 'setExit': track(doc, l.id).exit = op.exit; break;
          case 'setKeyframes': track(doc, l.id).keyframes = [...op.keyframes].sort((a, b) => a.atMs - b.atMs); break;
        }
      }
    }
  }
  const parsed = BannerDoc.safeParse(doc);
  if (!parsed.success) throw new OpError('המסמך לאחר העריכה אינו תקין: ' + parsed.error.issues.map((i) => i.message).join('; '));
  return parsed.data;
}

/** תקציר קריא של פעולות לתצוגה לפני apply */
export function describeOps(ops: EditOp[]): string[] {
  return ops.map((o) => {
    switch (o.op) {
      case 'move': return `הזזת ${o.layerId}`;
      case 'resize': return `שינוי גודל ${o.layerId}`;
      case 'scale': return `הגדלת ${o.layerId} פי ${o.factor}`;
      case 'setText': return `שינוי טקסט ${o.layerId}: "${o.text}"`;
      case 'setTiming': return `תזמון ${o.layerId}${o.startMs !== undefined ? ` התחלה ${o.startMs}ms` : ''}${o.endMs !== undefined ? ` סיום ${o.endMs ?? 'סוף'}` : ''}`;
      case 'setSpeed': return `מהירות כוללת x${o.factor}`;
      case 'setBannerTiming': return `תזמון באנר`;
      case 'setFont': return `פונט ${o.family} ב-${o.layerId}`;
      case 'setFontKey': return `פונט ${o.fontKey} ב-${o.layerId}`;
      case 'removeLayer': return `מחיקת ${o.layerId}`;
      case 'addShape': return `הוספת צורה ${o.fill}`;
      case 'addText': return `הוספת טקסט "${o.text}"`;
      default: return `${o.op}${'layerId' in o ? ' ' + o.layerId : ''}`;
    }
  });
}

export { AnimProps, Easing };
